import { store } from "@app-redux/store";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/600.css";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { persistStore } from "redux-persist";
import { PersistGate } from "redux-persist/integration/react";
import { MsalProvider } from "@azure/msal-react";
import { msalInstance, msalInitPromise } from "@/config/authConfig";
import { QueryClientProvider } from "@tanstack/react-query";
import { SnackbarProvider } from "notistack";
import { ThemeModeProvider } from "@components/ThemeModeProvider";
import { TelemetryProvider } from "@components/TelemetryProvider";
import { initTelemetry } from "@/telemetry/appInsights";
import { queryClient } from "./queryClient";
import { DEV_AUTH_BYPASS, seedQueryCache } from "@/devAuthBypass";
import App from "./App";

initTelemetry();

const container = document.getElementById("root") as HTMLElement;
const root = createRoot(container);
const persistor = persistStore(store);

// Wait for MSAL to initialize and handle any redirect response before rendering
const ready = DEV_AUTH_BYPASS ? Promise.resolve() : msalInitPromise;
ready.then(() => {
  if (DEV_AUTH_BYPASS) seedQueryCache(queryClient);

  root.render(
    <StrictMode>
      <ThemeModeProvider>
        <Provider store={store}>
          <PersistGate loading={null} persistor={persistor}>
            <MsalProvider instance={msalInstance}>
              <QueryClientProvider client={queryClient}>
                <SnackbarProvider
                  maxSnack={3}
                  autoHideDuration={4000}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                >
                  <BrowserRouter>
                    <TelemetryProvider>
                      <App />
                    </TelemetryProvider>
                  </BrowserRouter>
                </SnackbarProvider>
              </QueryClientProvider>
            </MsalProvider>
          </PersistGate>
        </Provider>
      </ThemeModeProvider>
    </StrictMode>
  );
});
